import {
  APP_INITIALIZER,
  EnvironmentProviders,
  inject,
  makeEnvironmentProviders,
} from '@angular/core';
import { Actions, ofType } from '@ngrx/effects';
import { stageActions } from '@core/actions/stage.actions';
import { AppConfigService } from '@core/services/config/app-config.service';
import { SignalRService } from '@core/services/signal-r/signal-r.service';
import { StudyPresenceStore } from './study-presence.store';

/**
 * Wires the SignalR study-presence observables to the StudyPresenceStore.
 *
 * This replaces the old ngrx Effects class — the signal store manages its own
 * state updates directly, and the surplus/denial dialog logic lives in the
 * store's methods.
 *
 * Returns immediately (non-blocking initializer) and subscribes to the
 * observables for the lifetime of the application.
 */
function initStudyPresenceSubscription(): void {
  const appConfig = inject(AppConfigService);
  if (!appConfig.config.activeReviewerTrackingEnabled) {
    return;
  }

  const signalR = inject(SignalRService);
  const store = inject(StudyPresenceStore);
  const actions = inject(Actions);

  // The store fires the surplus dialog from an in-store effect that watches
  // `presenceDisplayState` for transitions into a surplus state, so the
  // subscriber only needs to forward the snapshot.
  signalR.studyPresenceUpdated$.subscribe((snapshot) => {
    store.presenceUpdated(snapshot);
  });

  // Access-result stream carries both the granted and denied cases from
  // JoinStudyReview invocations (initial join + post-reconnect rejoin). The
  // snapshot inside each result is already pushed through studyPresenceUpdated$
  // by the SignalR service, so this subscription's job is to trigger the
  // access-denied dialog on denial.
  signalR.joinStudyReviewResult$.subscribe((result) => {
    const stageId = store.activeStageId();
    // projectId is not carried on the result — retrieve from the store's current context.
    if (stageId) {
      store.handleAccessResult(result, {
        projectId: store.activeProjectId() ?? '',
        stageId,
      });
    }
  });

  actions
    .pipe(ofType(stageActions.studyReviewAccessResultReceived))
    .subscribe(({ result, projectId, stageId }) => {
      store.presenceUpdated(result.snapshot);
      store.handleAccessResult(result, { projectId, stageId });
    });

  signalR.closeEvents$.subscribe(() => store.connectionLost());
  signalR.allReconnected$.subscribe(() => store.connectionRestored());
  signalR.rejoinFailed$.subscribe(() => store.rejoinFailed());

  // RPC-health channel — separate from transport. Hub-method exceptions
  // record a "first unacknowledged failure" timestamp; sustained failure
  // promotes into effective disconnect; recovery clears the timestamp.
  signalR.rpcFailureStarted$.subscribe(({ timestamp }) =>
    store.rpcFailureStarted(timestamp)
  );
  signalR.rpcRecovered$.subscribe(() => store.rpcRecovered());

  // Burst exhausted: drain transient flags only. Slot-state stays where it
  // was — the next snapshot or successful retry pins reality. We deliberately
  // do NOT route this into rejoinFailed(), since an exception is "we don't
  // know" rather than the server's typed "no, your slot is gone".
  signalR.invocationError$.subscribe(({ method }) => {
    if (method === 'JoinStudyReview') {
      store.rejoinInvocationFailed();
    }
  });
}

/**
 * Provides the APP_INITIALIZER that connects SignalR presence pushes
 * to the StudyPresenceStore signal store.
 */
export function provideStudyPresenceSubscription(): EnvironmentProviders {
  return makeEnvironmentProviders([
    {
      provide: APP_INITIALIZER,
      useFactory: () => initStudyPresenceSubscription,
      multi: true,
    },
  ]);
}
